import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

// Google Cast SDK types
declare global {
  interface Window {
    __onGCastApiAvailable: (isAvailable: boolean) => void;
    cast?: any;
    chrome?: any;
  }
}

export interface CastDevice {
  id: string;
  name: string;
  type: 'cast_video' | 'cast_audio';
  isConnected: boolean;
}

export interface CastState {
  isAvailable: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  currentDevice: CastDevice | null;
  availableDevices: CastDevice[];
}

@Injectable({
  providedIn: 'root'
})
export class CastService {
  private castStateSubject = new BehaviorSubject<CastState>({
    isAvailable: false,
    isConnected: false,
    isConnecting: false,
    currentDevice: null,
    availableDevices: []
  });

  public castState$ = this.castStateSubject.asObservable();
  
  private sessionReadySubject = new Subject<void>();
  public sessionReady$ = this.sessionReadySubject.asObservable();
  
  private castContext: any = null;
  private castSession: any = null;

  constructor() {
    this.initializeCastSDK();
  }

  private async initializeCastSDK(): Promise<void> {
    try {
      // Check if Cast SDK is already loaded
      if (window.cast && window.cast.framework) {
        this.setupCastFramework();
        return;
      }

      // Load the Cast SDK
      await this.loadCastSDK();
      
      // Wait for SDK to be ready
      window.__onGCastApiAvailable = (isAvailable: boolean) => {
        if (isAvailable) {
          this.setupCastFramework();
        } else {
          console.warn('Google Cast SDK not available');
          this.updateState({ isAvailable: false });
        }
      };
    } catch (error) {
      console.error('Failed to initialize Google Cast SDK:', error);
      this.updateState({ isAvailable: false });
    }
  }

  private loadCastSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (document.getElementById('cast-sdk')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'cast-sdk';
      script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Cast SDK'));
      document.head.appendChild(script);
    });
  }

  private setupCastFramework(): void {
    try {
      const castContext = window.cast.framework.CastContext.getInstance();
      
      // Configure Cast options
      const castOptions = new window.cast.framework.CastOptions();
      castOptions.receiverApplicationId = window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
      castOptions.autoJoinPolicy = window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED;
      
      // Set options
      castContext.setOptions(castOptions);
      
      // Listen for Cast state changes
      castContext.addEventListener(
        window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        (event: any) => this.onCastStateChanged(event)
      );

      // Listen for session changes
      castContext.addEventListener(
        window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event: any) => this.onSessionStateChanged(event)
      );

      this.castContext = castContext;
      this.updateState({ isAvailable: true });
      
      console.log('Google Cast SDK initialized successfully');
    } catch (error) {
      console.error('Failed to setup Cast framework:', error);
      this.updateState({ isAvailable: false });
    }
  }

  private onCastStateChanged(event: any): void {
    const castState = event.castState;
    console.log('Cast state changed:', castState);
    
    switch (castState) {
      case window.cast.framework.CastState.NO_DEVICES_AVAILABLE:
        this.updateState({ 
          isConnected: false, 
          isConnecting: false,
          currentDevice: null,
          availableDevices: []
        });
        break;
      case window.cast.framework.CastState.NOT_CONNECTED:
        this.updateState({ 
          isConnected: false, 
          isConnecting: false,
          currentDevice: null
        });
        break;
      case window.cast.framework.CastState.CONNECTING:
        this.updateState({ 
          isConnecting: true, 
          isConnected: false 
        });
        break;
      case window.cast.framework.CastState.CONNECTED:
        const session = this.castContext?.getCurrentSession();
        if (session) {
          this.castSession = session;
          const device: CastDevice = {
            id: session.getSessionId(),
            name: session.getCastDevice().friendlyName,
            type: 'cast_video', // Default to video, could be refined
            isConnected: true
          };
          this.updateState({ 
            isConnected: true, 
            isConnecting: false,
            currentDevice: device
          });
        }
        break;
    }
  }

  private onSessionStateChanged(event: any): void {
    const session = event.session;
    const sessionState = event.sessionState;
    
    console.log('Session state changed:', sessionState);
    
    switch (sessionState) {
      case window.cast.framework.SessionState.SESSION_STARTED:
      case window.cast.framework.SessionState.SESSION_RESUMED:
        this.castSession = session;
        // Notify that session is ready for media loading
        this.onSessionReady();
        break;
      case window.cast.framework.SessionState.SESSION_ENDED:
        this.castSession = null;
        this.updateState({ 
          isConnected: false, 
          currentDevice: null 
        });
        break;
    }
  }

  private onSessionReady(): void {
    // Emit event that Cast session is ready for media loading
    console.log('Cast session ready for media loading');
    this.sessionReadySubject.next();
  }

  private updateState(updates: Partial<CastState>): void {
    const currentState = this.castStateSubject.value;
    this.castStateSubject.next({ ...currentState, ...updates });
  }

  // Public API methods
  public isAvailable(): boolean {
    return this.castStateSubject.value.isAvailable;
  }

  public isConnected(): boolean {
    return this.castStateSubject.value.isConnected;
  }

  public getCurrentDevice(): CastDevice | null {
    return this.castStateSubject.value.currentDevice;
  }

  public async requestSession(): Promise<void> {
    if (!this.castContext) {
      throw new Error('Cast SDK not initialized');
    }

    try {
      await this.castContext.requestSession();
    } catch (error) {
      console.error('Failed to request Cast session:', error);
      throw error;
    }
  }

  public async endSession(): Promise<void> {
    if (!this.castSession) {
      return;
    }

    try {
      await this.castSession.endSession(true);
    } catch (error) {
      console.error('Failed to end Cast session:', error);
      throw error;
    }
  }

  public async startScreenShareWithAudio(): Promise<void> {
    if (!this.castSession) {
      throw new Error('No active Cast session');
    }

    try {
      console.log('Starting screen share with audio for casting...');
      
      // Request screen share with audio
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true, // We need video for screen share API
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          sampleRate: 44100,
          channelCount: 2
        }
      });

      // Check if audio track is available
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No audio track available. Make sure to check "Share audio" when selecting the screen/tab to share.');
      }

      console.log('Audio tracks found:', audioTracks.length);

      // Create media source for casting
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });

      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        console.log('Screen share recording stopped');
        const blob = new Blob(chunks, { type: 'video/webm' });
        // In a real implementation, you'd stream this to the Cast device
        console.log('Recorded blob size:', blob.size);
        
        // Clean up
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording
      mediaRecorder.start(1000); // Record in 1-second chunks

      // Store references for cleanup
      (this as any).screenStream = stream;
      (this as any).screenRecorder = mediaRecorder;

      console.log('Screen share with audio started successfully');
      return Promise.resolve();

    } catch (error) {
      console.error('Failed to start screen share with audio:', error);
      throw error;
    }
  }

  public stopScreenShare(): void {
    try {
      if ((this as any).screenRecorder) {
        (this as any).screenRecorder.stop();
        (this as any).screenRecorder = null;
      }
      
      if ((this as any).screenStream) {
        (this as any).screenStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
        (this as any).screenStream = null;
      }

      console.log('Screen share stopped');
    } catch (error) {
      console.error('Error stopping screen share:', error);
    }
  }

  public openChromeTabCasting(): void {
    // The simplest and most reliable approach is to guide users to Chrome's built-in cast feature
    // This provides full audio mirroring capabilities without complex implementation
    
    console.log('Directing user to Chrome\'s built-in tab casting');
    
    // We can't programmatically trigger Chrome's cast menu, but we can provide instructions
    // The user needs to:
    // 1. Right-click on the tab
    // 2. Select "Cast tab to..." or use the Cast button in Chrome's menu
    // 3. Choose their Chromecast device
    // 4. IMPORTANT: Check "Share audio" checkbox
    
    throw new Error('Tab casting steps:\n\n1. Right-click this tab\n2. Select "Cast tab to..."\n3. Choose your Chromecast device\n4. ⚠️ IMPORTANT: Check "Share audio" checkbox\n\nIf still no audio: Try casting your entire screen instead of just the tab.');
  }

  public supportsTabMirroring(): boolean {
    // Check if we're in Chrome and can access casting features
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    return isChrome && !!window.chrome;
  }

  public getTabMirroringInstructions(): string {
    if (!this.supportsTabMirroring()) {
      return 'Tab mirroring requires Google Chrome browser.';
    }
    
    return 'For full audio mirroring: Right-click this tab → "Cast tab to..." → Select your Chromecast device';
  }

  public async loadSpotifyTrack(track: any): Promise<void> {
    if (!this.castSession) {
      throw new Error('No active Cast session');
    }

    try {
      // Since Spotify tracks are protected, we need to use a different approach
      // For now, we'll try to use the preview URL if available
      const contentUrl = track.preview_url;
      
      if (!contentUrl) {
        // If no preview URL, we can't cast the track directly
        // In a production app, you'd need to implement server-side streaming
        // or use Spotify's partner Cast receiver
        throw new Error('Track cannot be cast (no preview available)');
      }

      const mediaInfo = new window.chrome.cast.media.MediaInfo(contentUrl, 'audio/mpeg');
      
      // Set up metadata
      const musicMetadata = new window.chrome.cast.media.MusicTrackMediaMetadata();
      musicMetadata.title = track.name;
      musicMetadata.artist = track.artists?.map((a: any) => a.name).join(', ') || 'Unknown Artist';
      musicMetadata.albumName = track.album?.name || 'Unknown Album';
      
      if (track.album?.images && track.album.images.length > 0) {
        musicMetadata.images = track.album.images.map((img: any) => 
          new window.chrome.cast.Image(img.url)
        );
      }
      
      mediaInfo.metadata = musicMetadata;

      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
      request.autoplay = true;
      
      console.log('Loading track on Cast device:', track.name);
      await this.castSession.loadMedia(request);
    } catch (error) {
      console.error('Failed to load Spotify track on Cast device:', error);
      throw error;
    }
  }

  public async loadMedia(contentUrl: string, metadata?: any): Promise<void> {
    if (!this.castSession) {
      throw new Error('No active Cast session');
    }

    try {
      const mediaInfo = new window.chrome.cast.media.MediaInfo(contentUrl, 'audio/mpeg');
      
      if (metadata) {
        const musicMetadata = new window.chrome.cast.media.MusicTrackMediaMetadata();
        musicMetadata.title = metadata.title;
        musicMetadata.artist = metadata.artist;
        musicMetadata.albumName = metadata.album;
        
        if (metadata.images && metadata.images.length > 0) {
          musicMetadata.images = metadata.images.map((img: any) => new window.chrome.cast.Image(img.url));
        }
        
        mediaInfo.metadata = musicMetadata;
      }

      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
      await this.castSession.loadMedia(request);
    } catch (error) {
      console.error('Failed to load media on Cast device:', error);
      throw error;
    }
  }

  public async playMedia(): Promise<void> {
    const media = this.castSession?.getMediaSession();
    if (!media) {
      throw new Error('No media loaded');
    }

    try {
      await media.play();
    } catch (error) {
      console.error('Failed to play media on Cast device:', error);
      throw error;
    }
  }

  public async pauseMedia(): Promise<void> {
    const media = this.castSession?.getMediaSession();
    if (!media) {
      throw new Error('No media loaded');
    }

    try {
      await media.pause();
    } catch (error) {
      console.error('Failed to pause media on Cast device:', error);
      throw error;
    }
  }

  public async setVolume(volume: number): Promise<void> {
    if (!this.castSession) {
      throw new Error('No active Cast session');
    }

    try {
      const volumeRequest = new window.chrome.cast.VolumeRequest();
      volumeRequest.level = Math.max(0, Math.min(1, volume));
      await this.castSession.setVolume(volumeRequest);
    } catch (error) {
      console.error('Failed to set volume on Cast device:', error);
      throw error;
    }
  }

  // Check if a track can be cast (has preview URL)
  public canCastTrack(track: any): boolean {
    return !!(track && track.preview_url);
  }

  // Get a user-friendly message about why a track can't be cast
  public getCastLimitationMessage(track: any): string {
    if (!track) {
      return 'No track selected';
    }
    if (!track.preview_url) {
      return 'This track has no preview available for casting. Only 30-second previews can be cast to Chromecast devices.';
    }
    return '';
  }

  // Get Cast devices (returns a mock list for now, as the Cast SDK doesn't expose device discovery)
  public getCastDevices(): CastDevice[] {
    const currentState = this.castStateSubject.value;
    
    // The Cast SDK doesn't expose available devices before connection
    // We can only show the current device if connected
    if (currentState.isConnected && currentState.currentDevice) {
      return [currentState.currentDevice];
    }
    
    return [];
  }
}